import React, { useContext, useState } from 'react';
import {
	Typography,
	Paper,
	Box,
	IconButton,
	Stack,
	Snackbar,
	Alert,
	styled as MuiStyled,
	useMediaQuery,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import FormOne from './FormOne';
import FormTwo from './FormTwo';
import FormThree from './FormThree';
import FormFour from './FormFour';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormFive from './FormFive';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { getSignUpPayload } from './utils';
import OTPAuth from '@src/components/OTPAuth/OTPAuth';
import { AxiosError } from 'axios';
import { AxiosResponse } from 'axios';
import { setCurrentUser } from '@src/store/reducers/currentUserReducer';
import { useAppDispatch } from '@src/store/hooks';
import CurrentUserResponse from '@src/api/responses/CurrentUserResponse';
import { setotpAuth } from '@src/store/reducers/otpAuthReducer';
import { storeToken } from '@src/helpers/StorageHelper';
import { LocalStorage } from '@src/storage/LocalStorage';
import { isValidPath } from '@src/helpers/Helpers';
import { setDefaultRestaurant } from '@src/store/reducers/branchReducer';
import { RESTAURANT } from '@src/Constants';
import { setRestaurants } from '@src/store/reducers/branchReducer';
import { useNavigate } from 'react-router-dom';

interface Option {
	id: string;
	label: string;
	value: string;
}

const StyledContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100vh;
	background: url('./register-bg.png') no-repeat center center;
	background-size: cover;
	padding: 40px 24px;
	overflow-y: auto;
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 32px;
	width: 100%;
	max-width: 400px;
	margin: 0 auto;
`;

export const FormSection = styled(Paper)`
	padding: 32px;
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

	@media screen and (max-width: 600px) {
		box-shadow: none !important;
		border-radius: 0 !important;
		border: none !important;
		padding: 16px !important;
		margin-top: 60px;
	}
`;

const SectionTitle = styled(Typography)`
	font-size: 24px;
	font-weight: 600;
	margin-bottom: 24px;
	color: #1a1a1a;
	position: relative;
	padding-bottom: 12px;

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 60px;
		height: 3px;
		background: var(--primary-color);
		border-radius: 2px;
	}
`;

export const FooterAction = MuiStyled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	padding: '16px',
	background: 'white',
	borderTop: '1px solid #e0e0e0',
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	[theme.breakpoints.up('sm')]: {
		position: 'relative',
		bottom: 0,
		padding: '0px',
	},
}));

const validationSchema = Yup.object().shape({
	// Basic Info
	customerName: Yup.string().required('Customer name is required'),
	mobileNumber: Yup.string()
		.matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
		.required('Mobile number is required'),
	state: Yup.string().required('State is required'),
	district: Yup.string().required('District is required'),
	town: Yup.string().required('Town is required'),
	pincode: Yup.string().matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
	address_1: Yup.string(),
	address_2: Yup.string(),
	// Company Details
	isCompany: Yup.boolean(),
	businessName: Yup.string().when('isCompany', {
		is: true,
		then: (schema) => schema.required('Business name is required'),
	}),
	preferredDRP: Yup.string().required('Preferred DRP is required'),
	// isGstCompliant: Yup.boolean(),
	// gstNumber: Yup.string().when('isGstCompliant', {
	// 	is: true,
	// 	then: (schema) => schema.required('GST number is required'),
	// }),
	// billingAddress: Yup.string().when('isGstCompliant', {
	// 	is: true,
	// 	then: (schema) => schema.required('Billing address is required'),
	// }),
	// Machinery Details
	// machines: Yup.array().of(
	// 	Yup.object().shape({
	// 		machineBrand: Yup.string().required('Machine brand is required'),
	// 		model: Yup.string().required('Model is required'),
	// 		yearOfManufacture: Yup.string()
	// 			.matches(/^\d{4}$/, 'Year must be 4 digits')
	// 			.required('Year of manufacture is required'),
	// 		hoursRun: Yup.string()
	// 			.matches(/^\d+$/, 'Hours run must be a number')
	// 			.required('Hours run is required'),
	// 	})
	// ),
	// habits: Yup.object().shape({
	// preferredDRP: Yup.string().required('Preferred DRP is required'),
	// partsPurchaser: Yup.string().required('Parts purchaser selection is required'),
	// purchaseMode: Yup.array()
	// 	.min(1, 'Select at least one purchase mode')
	// 	.required('Purchase mode is required'),
	// purchaseFrequency: Yup.string().required('Purchase frequency is required'),
	// commonParts: Yup.string()
	// 	.required('Commonly purchased parts is required')
	// 	.min(10, 'Please provide more details about commonly purchased parts'),
	// monthlySpend: Yup.number()
	// 	.required('Monthly spend is required')
	// 	.min(0, 'Monthly spend cannot be negative')
	// 	.typeError('Monthly spend must be a number'),
	// }),
	// additionalInfo: Yup.object().shape({
	// ageGroup: Yup.string().required('Age group is required'),
	// preferredLanguage: Yup.string().required('Preferred language is required'),
	// machinePlans: Yup.string().required('Machine plans selection is required'),
	// interestedInFinance: Yup.string().required('Interested in finance selection is required'),
	// preferredServiceChannel: Yup.string().required('Preferred service channel is required'),
	// partsUsagePreference: Yup.string().required('Parts usage preference is required'),
	// optInForOffers: Yup.boolean(),
	// preferredContactTime: Yup.string().required('Preferred contact time is required'),
	// nearestLandmark: Yup.string()
	// 	.required('Nearest landmark is required')
	// 	.min(5, 'Please provide more details for the landmark'),
	// deliveryAvailability: Yup.string().required('Delivery availability is required'),
	// referredBy: Yup.string().min(3, 'Please provide a valid referral name'),
	// npsScore: Yup.number()
	// 	.min(0, 'Score must be between 0 and 10')
	// 	.max(10, 'Score must be between 0 and 10')
	// 	.required('NPS score is required'),
	// usesWhatsApp: Yup.string().required('WhatsApp usage information is required'),
	// interestedInAppDemo: Yup.boolean(),
	// }),
});

export function FormHeader({ title, onBackClick }: any) {
	return (
		<Stack
			alignItems={'center'}
			direction={'row'}
			mb={2}
			justifyContent={'space-between'}
			sx={{
				position: { xs: 'fixed', md: 'relative' },
				top: 0,
				left: 0,
				right: 0,
				padding: { xs: '16px', md: '0' },
				backgroundColor: 'white',
				zIndex: 100,
				boxShadow: { xs: '0 2px 4px rgba(0,0,0,0.1)', md: 'none' },
			}}
		>
			<IconButton sx={{ '&:hover': { bgcolor: 'primary.main' } }} onClick={onBackClick}>
				<ArrowBackIcon style={{ color: 'black' }} />
			</IconButton>
			<Typography fontSize={20} fontWeight={700}>
				{title}
			</Typography>
			<Box sx={{ width: 40 }}></Box>
		</Stack>
	);
}

const initialValues = {
	customerName: '',
	mobileNumber: '',
	state: '',
	district: '',
	town: '',
	pincode: '',
	address_1: '',
	address_2: '',
	preferredDRP: '',
	isCompany: false,
	businessName: '',
};

function Register() {
	const { apiService } = useContext(ApplicationContext);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [savedData, setSavedData] = useState(null);
	const [error, setError] = useState<string>('');
	const [otpExpiryTime, setOtpExpiryTime] = useState<number>(-1);
	const [otp, setOtp] = useState<string[]>(['', '', '', '']);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isMobile = useMediaQuery('(max-width: 600px)');

	const handleErrorClose = () => {
		setIsErrorVisible(false);
		setErrorMessage('');
	};
	function callSignUp(values) {
		const payload = getSignUpPayload(values);
		// console.log({ payload });
		setLoading(true);

		apiService?.signUpCall(
			payload,
			(res) => {
				console.log(res);
				setSavedData(res);
				setCurrentPage(0);
				setLoading(false);
			},
			(err) => {
				console.log(err);
				setIsErrorVisible(true);
				setErrorMessage(err?.response?.data?.message || 'Something went wrong');
				setLoading(false);
			}
		);
	}

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema,
		onSubmit: async (values) => {
			try {
				callSignUp(values);
			} catch (error) {
				console.error(error);
			}
		},
	});

	const addMachine = () => {
		const machines = [...formik.values.machines];
		machines.push({
			machineBrand: '',
			model: '',
			yearOfManufacture: '',
			hoursRun: '',
		});
		formik.setFieldValue('machines', machines);
	};

	const removeMachine = (index: number) => {
		const machines = [...formik.values.machines];
		machines.splice(index, 1);
		formik.setFieldValue('machines', machines);
	};

	const getMachineErrorText = (index: number, field: string): string | undefined => {
		const error =
			formik.errors.machines?.[index]?.[field as keyof typeof formik.errors.machines[0]];
		const touched =
			formik.touched.machines?.[index]?.[field as keyof typeof formik.touched.machines[0]];
		return touched && error ? String(error) : undefined;
	};

	const getErrorText = (field: string): string | undefined => {
		const error = formik.errors[field as keyof typeof formik.errors];
		const touched = formik.touched[field as keyof typeof formik.touched];
		return touched && error ? String(error) : undefined;
	};

	const getBranch = (path, type) => {
		try {
			if (type === RESTAURANT) {
				apiService.getRestaurants(
					(response: AxiosResponse) => {
						const data = response?.data?.data;
						const branch = data?.find((item: any) => item?.id === Number(path?.branchId));

						if (branch) {
							dispatch(setRestaurants(data));
							dispatch(setDefaultRestaurant(branch));
							const localStorage = new LocalStorage();
							localStorage.removeItem('prevPath');
							navigate(path?.path);
						} else {
							navigate('/restaurants');
						}
					},
					(error: AxiosError) => {}
				);
			} else {
				apiService.getAllSuppliers(
					(response: AxiosResponse) => {
						const data = response?.data?.data;
						const branch = data?.find((item: any) => item?.id === Number(path?.branchId));

						if (branch) {
							dispatch(setRestaurants(data));
							dispatch(setDefaultRestaurant(branch));
							const localStorage = new LocalStorage();
							localStorage.removeItem('prevPath');
							navigate(path?.path);
						} else {
							navigate('/suppliers');
						}
					},
					(error: AxiosError) => {}
				);
			}
		} catch (err) {
			if (type === RESTAURANT) {
				navigate('/restaurants');
			} else {
				navigate('/suppliers');
			}
		} finally {
			setError('');
			setLoading(false);
		}
	};

	const handleOTPSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoading(true);
		const formattedNumber = '+91' + formik.values.mobileNumber;
		const data = {
			phone: formattedNumber,
			otp: otp?.join(''),
		};
		apiService.loginCall(
			data,
			(response: AxiosResponse) => {
				const currentUserResponse = new CurrentUserResponse(response.data.data.user);
				dispatch(setCurrentUser(currentUserResponse.currentUser));
				dispatch(
					setotpAuth({
						phoneNumber: '',
						expiryTime: '',
					})
				);

				// Store token in local storage
				storeToken(response?.data?.data?.token);

				// Android web interface
				// if (window.AndroidWebInterface) {
				// 	window.AndroidWebInterface.registerUserToken(response.data.data.token ?? '');
				// }
				// if (response?.data?.data?.onBoarding && isMobilWidth) {
				// 	setIsOnboardingVisible(true);
				// 	return;
				// }

				//Navigate to route from where you logout or hit the url
				const localStorage = new LocalStorage();
				const storedPrevPathJSON = localStorage.getItem('prevPath');
				const path = JSON.parse(storedPrevPathJSON);
				if (path?.path) {
					if (isValidPath(currentUserResponse?.currentUser?.type?.toLowerCase(), path?.path)) {
						getBranch(path, currentUserResponse?.currentUser?.type?.toLowerCase());
						return;
					}
				}

				localStorage.removeItem('prevPath');
				setError('');
				setLoading(false);
				if (currentUserResponse?.currentUser?.type?.toLowerCase() === RESTAURANT) {
					navigate('/restaurants');
				} else {
					// navigate(`/dashboard`);
					// navigate(`/chats`);
					navigate('/suppliers');
				}
			},
			(error: AxiosError) => {
				setLoading(false);
				setError('Please enter a valid code');
			}
		);
	};

	const mobileStyles = isMobile
		? {
				background: 'none',
				padding: 0,
		  }
		: {};

	return (
		<StyledContainer
			sx={{
				...mobileStyles,
			}}
		>
			<StyledForm onSubmit={formik.handleSubmit}>
				{/* Section 1: Basic Information */}
				{currentPage === 1 && (
					<FormOne
						formik={formik}
						getErrorText={getErrorText}
						goToNextPage={() => setCurrentPage(2)}
					/>
				)}
				{/* {currentPage === 2 && (
					<FormTwo
						formik={formik}
						getErrorText={getErrorText}
						goToNextPage={() => setCurrentPage(3)}
						goToPreviousPage={() => setCurrentPage(1)}
					/>
				)}
				{currentPage === 3 && (
					<FormThree
						formik={formik}
						addMachine={addMachine}
						removeMachine={removeMachine}
						getMachineErrorText={getMachineErrorText}
						goToNextPage={() => setCurrentPage(4)}
						goToPreviousPage={() => setCurrentPage(2)}
					/>
				)}
				{currentPage === 4 && (
					<FormFour
						formik={formik}
						getErrorText={getErrorText}
						goToNextPage={() => setCurrentPage(5)}
						goToPreviousPage={() => setCurrentPage(3)}
					/>
				)}

				{currentPage === 5 && (
					<FormFive
						formik={formik}
						getErrorText={getErrorText}
						goToNextPage={() => formik.handleSubmit()}
						goToPreviousPage={() => setCurrentPage(4)}
						loading={loading}
					/>
				)} */}

				{savedData && (
					<Paper sx={{ padding: '16px', marginTop: { xs: '20vh', md: '0' } }}>
						<OTPAuth
							step={'2'}
							updateStepper={() => {
								setCurrentPage(1);
								setSavedData(null);
							}}
							loginError={''}
							error={error}
							setError={setError}
							countryCode={'+91'}
							phoneNumber={''}
							setPhoneNumber={() => {}}
							otp={otp}
							setOtp={setOtp}
							loading={loading}
							handleSubmit={() => {}}
							handleOTPSubmit={handleOTPSubmit}
						/>
					</Paper>
				)}

				<Snackbar
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					open={isErrorVisible}
					autoHideDuration={6000}
					onClose={handleErrorClose}
				>
					<Alert onClose={handleErrorClose} severity="error" elevation={4}>
						{errorMessage}
					</Alert>
				</Snackbar>
			</StyledForm>
		</StyledContainer>
	);
}

export default Register;
